import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import OfflineBoltOutlinedIcon from '@mui/icons-material/OfflineBoltOutlined'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import PollOutlinedIcon from '@mui/icons-material/PollOutlined'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import RadioOutlinedIcon from '@mui/icons-material/RadioOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { HEALTH_PROFESSIONAL_COMPANY_TYPES } from '@/types/CompanyType'
import { EntityList } from '@/types/Entity'

/**
 * Groupes repliables de la sidebar. Une entrée de route portant
 * `menuGroup: MENU_GROUPS.X` est rendue DANS la section correspondante (repliable) ;
 * les entrées sans `menuGroup` restent à plat.
 *
 * ATTENTION — les groupes n'existent QU'EN MENU V2 :
 * - Menu v1 (« Classique, par entité ») : menu strictement à plat, trié par `position`.
 *   `useRouteActive` neutralise `menuGroup` (aucune section, comportement historique).
 * - Menu v2 (unifié, flag `menu_version`=2) : le filtre par entité disparaît et les routes
 *   sans `menuGroup` explicite sont groupées PAR ENTITÉ (cf. deriveMenuGroupV2) ; l'ordre
 *   des groupes suit `sectionPosition` du catalogue ci-dessous.
 */
export const MENU_GROUPS = {
  /** Annuaires : pharmacies, cartes, équipes, entreprises, étudiants, utilisateurs, admins. */
  DIRECTORY: 'directory',
  /** Suivi & pilotage : CRM, commandes, finance, contrats, campagnes, points, conditions. */
  MONITORING: 'monitoring',
  /** Call center : procédures, stats, priorités, production, relation pharmacien, tâches. */
  CALL_CENTER: 'call_center',
  /** Gestion RH : liste des administrateurs, attestations & formations internes. */
  RH: 'rh',
  /** Outils : companion, Merlin, paramétrage. */
  TOOLS: 'tools',
  /** Menu v2 : section commune « Ma pharmacie » (contrats, factures, équipe, profil). */
  PHARMACY: 'pharmacy',
  /** Menu v2 : section « Mon groupement » (managers groupement/chaîne). */
  GROUPEMENT: 'groupement_space',
  /** Menu v2 : pseudo-groupe « Actions à faire » (entrées actionables à badge > 0). */
  ACTIONS: 'actions',
  /** Espace vendeur Store : produits, stock, laboratoires, promotions. */
  VENDOR_CATALOG: 'vendor_catalog',
  /** Espace vendeur Store : statistiques, comptabilité, refus LGO. */
  VENDOR_RESULTS: 'vendor_results',
  /** Espace vendeur Store : paiement, abonnement, contrat, livraison. */
  VENDOR_ACCOUNT: 'vendor_account',
} as const

/** Groupes v2 dérivés de l'entité d'une route (clé stable `entity:<EntityList>`). */
export const entityMenuGroupKey = (entity: EntityList): string => `entity:${entity}`

/**
 * Item de NIVEAU 1, hors de toute section (`menuFlat`) :
 * - `true`    → en HAUT, avant les sections (ex. « Tableau de bord ») ;
 * - `'bottom'` → en BAS, après toutes les sections (ex. « Ma cagnotte », « Mes tickets » :
 *   ce qui suit l'utilisateur et non un module).
 */
export type MenuFlat = boolean | 'bottom'

/** Pseudo-position de section des items à plat (cf. sectionComparator de useRouteActive). */
export const FLAT_SECTION_TOP = 5
export const FLAT_SECTION_BOTTOM = 9998

export const getFlatSectionPosition = (menuFlat: MenuFlat | undefined): number =>
  menuFlat === 'bottom' ? FLAT_SECTION_BOTTOM : FLAT_SECTION_TOP

/**
 * Où poser le séparateur léger du menu : devant le bloc d'entrées à plat du bas (« Ma cagnotte »,
 * « Mes tickets »), qui suivent la personne et non un module. Sans lui, « Ma cagnotte » se lit
 * comme une entrée de la dernière section (Cahier School II.7.a : « située dans Mes missions »).
 *
 * Rend `null` s'il n'y a rien à séparer : aucune section au-dessus (le menu à plat v1 trie ces
 * entrées par `position`, elles n'y forment pas un bloc), ou aucun bloc du bas.
 */
export const getBottomBlockStart = (
  entries: ReadonlyArray<{ isGroup: boolean; isBottom: boolean }>
): number | null => {
  let start = entries.length
  while (start > 0 && entries[start - 1].isBottom) start -= 1

  if (start === entries.length || !entries.slice(0, start).some(entry => entry.isGroup)) return null

  return start
}

export type MenuGroupKey = string

export type MenuGroupConfig = {
  t: string
  text: string
  icon: React.ElementType
  /** Couleur de l'icone dans la navbar (theme d'entite) — blanc translucide par defaut. */
  color?: string
  /**
   * Ordre de la section en menu v2 (croissant). Les groupes transverses BO (directory/
   * monitoring/tools) gardent leur ordre par position de première entrée en v1 ; en v2
   * ils sont rendus après les groupes d'entité.
   */
  sectionPosition?: number
}

const STATIC_MENU_GROUP_CONFIG: Record<string, MenuGroupConfig> = {
  [MENU_GROUPS.ACTIONS]: {
    color: '#FFA726',
    t: 'components.navMenu.group.actions',
    text: 'Actions à faire',
    icon: OfflineBoltOutlinedIcon,
    sectionPosition: 0,
  },
  [MENU_GROUPS.PHARMACY]: {
    t: 'components.navMenu.group.pharmacy',
    text: 'Ma pharmacie',
    icon: LocalPharmacyOutlinedIcon,
    sectionPosition: 10,
  },
  [MENU_GROUPS.GROUPEMENT]: {
    t: 'components.navMenu.group.groupement',
    text: 'Mon groupement',
    icon: BusinessCenterOutlinedIcon,
    sectionPosition: 10,
  },
  [MENU_GROUPS.DIRECTORY]: {
    color: '#A5B4FC',
    t: 'components.navMenu.group.directory',
    text: 'Annuaires',
    icon: FolderSharedOutlinedIcon,
    // Menu v2 : juste sous « Actions à faire » (0) et le home à plat (5) — demande admin.
    sectionPosition: 6,
  },
  [MENU_GROUPS.MONITORING]: {
    color: '#4DD0E1',
    t: 'components.navMenu.group.monitoring',
    text: 'Suivi & pilotage',
    icon: QueryStatsOutlinedIcon,
    // Juste sous « Annuaires » (6) — demande admin 2026-07-19 (avant : 110, apres les entites).
    sectionPosition: 7,
  },
  [MENU_GROUPS.CALL_CENTER]: {
    color: '#62C7FF',
    t: 'components.navMenu.group.callCenter',
    text: 'Call center',
    icon: PhoneInTalkOutlinedIcon,
    // Juste apres « Suivi & pilotage » (7) — demande produit : Call center rattache au suivi,
    // sa propre section mais remontee au-dessus des groupes de modules (avant : 115, en bas).
    sectionPosition: 8,
  },
  [MENU_GROUPS.RH]: {
    color: '#F48FB1',
    t: 'components.navMenu.group.rh',
    text: 'Gestion RH',
    icon: BadgeOutlinedIcon,
    // Section interne (équipe, pas modules) : après les groupes d'entité, avant « Outils ».
    sectionPosition: 110,
  },
  // Espace vendeur : le quotidien est à plat en tête (`menuFlat`), le reste se replie dessous. Le menu
  // s'arrête à deux niveaux : ce sont des sections, pas des sous-menus de « Ma centrale ».
  [MENU_GROUPS.VENDOR_CATALOG]: {
    color: '#FFC645',
    t: 'components.navMenu.group.vendorCatalog',
    text: 'Catalogue',
    icon: CategoryOutlinedIcon,
    sectionPosition: 51,
  },
  [MENU_GROUPS.VENDOR_RESULTS]: {
    color: '#FFC645',
    t: 'components.navMenu.group.vendorResults',
    text: 'Résultats',
    icon: QueryStatsOutlinedIcon,
    sectionPosition: 52,
  },
  [MENU_GROUPS.VENDOR_ACCOUNT]: {
    color: '#FFC645',
    t: 'components.navMenu.group.vendorAccount',
    text: 'Mon compte vendeur',
    icon: ManageAccountsOutlinedIcon,
    sectionPosition: 53,
  },
  [MENU_GROUPS.TOOLS]: {
    t: 'components.navMenu.group.tools',
    text: 'Outils',
    icon: HandymanOutlinedIcon,
    sectionPosition: 120,
  },
}

/**
 * Catalogue des groupes d'entité (menu v2). Care et School sont volontairement en BAS
 * (sectionPosition élevée) : rattachés au USER (missions, formations) plus qu'à la
 * pharmacie — le pilotage d'équipe reste, lui, dans « Ma pharmacie ».
 * Libellés : réutilise les clés `common.dashboard.entityName.*` déjà traduites (5 locales).
 */
const ENTITY_MENU_GROUP_CONFIG: Partial<Record<EntityList, MenuGroupConfig>> = {
  // Audience ADMIN uniquement (cf. deriveMenuGroupV2) : cote pharmacien, PHARMEDIGROUP
  // est assimile au groupe commun « Ma pharmacie ».
  [EntityList.PHARMEDIGROUP]: {
    color: '#12AAFF',
    t: 'common.dashboard.entityName.pharmedigroup',
    text: 'PharmediGroup',
    icon: LocalPharmacyOutlinedIcon,
    sectionPosition: 15,
  },
  [EntityList.PHARMEDISOUND]: {
    color: '#F96C62',
    t: 'common.dashboard.entityName.pharmedisound',
    text: 'Ma radio',
    icon: RadioOutlinedIcon,
    sectionPosition: 20,
  },
  [EntityList.PHARMEDINSIGHT]: {
    color: '#0ADDBD',
    t: 'common.dashboard.entityName.pharmedinsight',
    text: 'Mes enquêtes',
    icon: PollOutlinedIcon,
    sectionPosition: 30,
  },
  [EntityList.PHARMEDISTOREBAG]: {
    color: '#FFC645',
    t: 'common.dashboard.entityName.pharmedistorebag',
    text: 'Ma PLV / Sacherie',
    icon: ShoppingBagOutlinedIcon,
    sectionPosition: 40,
  },
  [EntityList.PHARMEDISTORE]: {
    color: '#FFC645',
    t: 'common.dashboard.entityName.pharmedistore',
    text: "Mon hub d'achat",
    icon: StorefrontOutlinedIcon,
    sectionPosition: 50,
  },
  [EntityList.PHARMEDICALL]: {
    color: '#62C7FF',
    t: 'common.dashboard.entityName.pharmedicall',
    text: 'Call',
    icon: PhoneInTalkOutlinedIcon,
    sectionPosition: 60,
  },
  [EntityList.CEGIDFLEX]: {
    color: '#B4B7CC',
    t: 'common.dashboard.entityName.cegidflex',
    text: 'Cegidflex',
    icon: BusinessCenterOutlinedIcon,
    sectionPosition: 70,
  },
  // Section « perso » (liée au user, pas à la pharmacie) : en bas du menu.
  [EntityList.PHARMEDISCHOOL]: {
    color: '#64C07D',
    t: 'common.dashboard.entityName.pharmedischool',
    text: 'Mes formations',
    icon: SchoolOutlinedIcon,
    sectionPosition: 80,
  },
  [EntityList.PHARMEDICARE]: {
    color: '#8B5CF6',
    t: 'common.dashboard.entityName.pharmedicare',
    text: 'Mes missions',
    icon: LocalHospitalOutlinedIcon,
    sectionPosition: 90,
  },
}

/** Rétro-compat : catalogue historique consommé par le rendu v1 (BO uniquement). */
export const MENU_GROUP_CONFIG: Record<string, MenuGroupConfig> = STATIC_MENU_GROUP_CONFIG

/**
 * Résolution UNIVERSELLE d'un groupe (statique OU entité `entity:<EntityList>`).
 * Retourne undefined pour une clé inconnue -> l'entrée est rendue à plat.
 */
/**
 * Section commune vue par un professionnel de santé hors officine (kiné, médecin…) :
 * même contenu que « Ma pharmacie » (contrats, factures, profil), mais ce n'est pas une
 * pharmacie.
 */
const PRACTICE_MENU_GROUP_CONFIG: MenuGroupConfig = {
  t: 'components.navMenu.group.practice',
  text: 'Mon cabinet',
  icon: LocalHospitalOutlinedIcon,
  sectionPosition: 10,
}

/**
 * @param companyType type de la structure de l'utilisateur (`selectCompanyDefault.type`) :
 *                    adapte le libellé de la section commune « Ma pharmacie ».
 */
export const getMenuGroupConfig = (
  key: MenuGroupKey | undefined,
  companyType?: string | null
): MenuGroupConfig | undefined => {
  if (!key) return undefined
  if (
    key === MENU_GROUPS.PHARMACY &&
    HEALTH_PROFESSIONAL_COMPANY_TYPES.includes(companyType as (typeof HEALTH_PROFESSIONAL_COMPANY_TYPES)[number])
  ) {
    return PRACTICE_MENU_GROUP_CONFIG
  }
  if (STATIC_MENU_GROUP_CONFIG[key]) return STATIC_MENU_GROUP_CONFIG[key]
  if (key.startsWith('entity:')) {
    return ENTITY_MENU_GROUP_CONFIG[key.slice('entity:'.length) as EntityList]
  }
  return undefined
}

/** Position de section d'un groupe (v2) — groupes inconnus en fin. */
export const getMenuGroupSectionPosition = (key: MenuGroupKey | undefined): number =>
  getMenuGroupConfig(key)?.sectionPosition ?? 9999

/** Audience du menu v2 : pilote le libellé/regroupement des routes PHARMEDIGROUP. */
export type MenuAudience = 'pharmacy' | 'admin' | 'groupement'

/**
 * Groupe v2 d'une route : `menuGroup` explicite prioritaire (commun « Ma pharmacie »,
 * transverses BO), sinon dérivé de la PREMIÈRE entité de la route.
 *
 * - Routes de l'ESPACE LABO (`space: 'industry'`) : jamais dérivées — le menu labo garde
 *   sa structure à plat existante (les groupes de modules labo arriveront avec leur
 *   `menuGroup` explicite).
 * - Audience `pharmacy` : PHARMEDIGROUP = groupe commun « Ma pharmacie » (dashboard,
 *   équipe, contrats, factures y vivent déjà) ; une route `home` PHARMEDIGROUP reste À
 *   PLAT (tableau de bord global en tête de menu).
 * - Audience `admin` (backoffice) : PHARMEDIGROUP = groupe d'entité « PharmediGroup »
 *   (« Ma pharmacie » n'aurait aucun sens pour un admin) ; les transverses
 *   Annuaires/Suivi/Outils restent portés par leur `menuGroup` explicite.
 * - Audience `groupement` (managers groupement/chaîne) : PHARMEDIGROUP = groupe
 *   « Mon groupement » (leurs pages parlent du parc, pas d'une officine).
 */
export const deriveMenuGroupV2 = (
  route: {
    menuGroup?: MenuGroupKey
    entity?: EntityList[]
    home?: boolean
    space?: string
    menuFlat?: MenuFlat
  },
  audience: MenuAudience = 'pharmacy'
): MenuGroupKey | undefined => {
  // menuFlat : item de NIVEAU 1 force (a plat, jamais re-derive dans un groupe).
  if (route.menuFlat) return undefined
  if (route.menuGroup) return route.menuGroup
  if (route.space === 'industry') return undefined
  const entity = route.entity?.[0]
  if (!entity) return undefined
  if (entity === EntityList.PHARMEDIGROUP) {
    if (route.home) return undefined
    if (audience === 'admin') return entityMenuGroupKey(entity)

    return audience === 'groupement' ? MENU_GROUPS.GROUPEMENT : MENU_GROUPS.PHARMACY
  }
  return entityMenuGroupKey(entity)
}

/**
 * Fiche close (plan « une fiche par SIREN ») : l'ancien titulaire consulte SA période, il
 * n'active plus rien. Seule reste la section « Ma pharmacie » (entreprise, contrats,
 * facturation, abonnement, équipements, équipe) et les entrées à plat qui ne relèvent
 * d'aucun module (accueil, tickets). Radio, enquêtes, PLV, centrale, formations, missions
 * Care : retirés, ils n'ont plus d'objet sur une période close.
 */
export const isRouteOpenOnClosedCompany = (
  route: { entity?: EntityList[] },
  menuGroup: MenuGroupKey | undefined
): boolean => {
  if (menuGroup) return menuGroup === MENU_GROUPS.PHARMACY
  const entity = route.entity?.[0]

  return !entity || entity === EntityList.PHARMEDIGROUP
}

/** Clé localStorage de l'état ouvert/fermé des groupes (partagé entre sessions). */
export const MENU_GROUPS_STORAGE_KEY = 'pharmedigroup-navbar-groups-open'
